import { useQuery } from "@apollo/client";
import { isEmpty, orderBy } from "lodash";
import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useTranslation } from "react-i18next";

import { WORDLE_REPORT } from "../../../graphql/queries/ReportQueries";
import { GameForReport } from "../GameReport/GameReport";
import { ChartHeader } from "../ChartHeader";
import { ChartTooltip } from "../ChartTooltip";
import { WithQueryLoader } from "../../shared/WithQueryLoader";

import styles from "./WordleChart.module.scss";

export interface WordleChartProps {
  game: GameForReport;
}

export const WordleChart = ({ game }: WordleChartProps) => {
  const { t } = useTranslation();
  const { data, loading } = useQuery(WORDLE_REPORT, {
    variables: {
      gameId: game.id,
    },
  });

  const transformedData = useMemo(() => {
    if (!data?.wordleReport) {
      return [];
    }

    return orderBy(data.wordleReport.scoreResults, "try", "asc");
  }, [data]);

  if (isEmpty(transformedData)) {
    return <div className={styles.emptyWrapper}>{t("report:noData")}</div>;
  }

  return (
    <WithQueryLoader isLoading={loading}>
      <div className={styles.container}>
        <ChartHeader respondersCount={data?.wordleReport.totalResponses}>{t("report:wordle.header")}</ChartHeader>

        <ResponsiveContainer width="100%" height="100%" minHeight={400}>
          <BarChart
            layout="vertical"
            width={500}
            height={400}
            data={transformedData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <XAxis type="number" tickLine={false} orientation="top" />
            <YAxis width={75} type="category" dataKey="score" tickLine={false} />

            <Tooltip content={<ChartTooltip translation="report:wordle.tooltip" />} cursor={{ fill: "#E8E8E8" }} />
            <Bar dataKey="count" fill="#F6A16F" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </WithQueryLoader>
  );
};
